import {
    el, setAttr, mount, setChildren, setStyle
} from 'redom';
import isEqual from 'lodash/isEqual';

import { ContentsManager } from '../Content/contentsManager';
import {
    ContentModel, IVariantModel
} from '../Content/contentModel';
import { ConfigurationTranslation } from './configurationTranslation';
import { ITextGeometry, IVariantText } from '../Content/Pattern/Decal/patternText';
import { IMaskGeometry, IVariantMask } from '../Content/Pattern/Decal/patternMask';
import { IVariantMeshesMaterials, IVariantMeshesProperties } from '../Content/contentModelVariantMesh';
import { SystemCamera } from '../System/systemCamera';

import './variantMesh.sass';
import {
    ARROW_ICON, CROSS_ICON, MENU_ICON, VALID_ICON
} from '../Ui/icons';

const setElementSelected = (button: HTMLElement): void => {
    const parent = button.parentNode;
    const children = parent.childNodes;
    for (let i = 0; i < children.length; i++) {
        const child = children[i];
        setAttr(child, { selected: false });
    }
    setAttr(button, { selected: true });
};

export class ConfigurationVariantMesh {
    private system: SystemCamera;

    private container: HTMLElement;

    private configurationTranslation: ConfigurationTranslation

    private contentsManager: ContentsManager

    // CONFIG LAYER
    private configurationContainer: HTMLElement

    private variantContainer: HTMLElement

    private previusTitle1: HTMLElement

    private previusTitle2: HTMLElement

    private meshTitle: HTMLElement

    private upButton: HTMLElement

    private upArrow: HTMLElement

    private downButton: HTMLElement

    private downArrow: HTMLElement

    private materialTitle: HTMLElement

    private nextTitle1: HTMLElement

    private nextTitle2: HTMLElement

    private hideButton: HTMLElement

    private collapseArrow: HTMLElement

    // MENU LAYER
    private enterMenuButton: HTMLElement

    private quitMenuButton: HTMLElement

    private menu: HTMLElement

    private menuList: HTMLElement

    // SUMMARY LAYER
    private summaryContainer: HTMLElement

    private title: HTMLElement

    private subTitle: HTMLElement

    private price: HTMLElement

    public confirm: HTMLElement

    // OTHER
    public exit: HTMLElement

    private visibleStyle = { opacity: 1, 'pointer-events': 'visible' }

    private unvisibleStyle = { opacity: 0, 'pointer-events': 'none' }

    constructor(
        configurationTranslation: ConfigurationTranslation,
        container: HTMLElement,
        system: SystemCamera,
        contentsManager: ContentsManager
    ) {
        this.container = container;
        this.system = system;
        this.configurationTranslation = configurationTranslation;
        this.contentsManager = contentsManager;
        this.configurationTranslation.onChange(() => {
            this.updateTranslations();
        });

        setAttr(container, { 'data-naker-responsive': true, 'data-naker-configurator-collapsed': false });

        this.buildConfiguration();
        this.buildSummary();
        this.addMeshPick(system);
    }

    private buildConfiguration() {
        this.configurationContainer = el('div.naker-configurator-container', [
            this.enterMenuButton = el('div.naker-button.naker-menu-button',
                { onclick: () => { this.checkMenu(); } }),
            this.menu = el('div.naker-menu', { style: this.unvisibleStyle }, [
                this.quitMenuButton = el('div.naker-button.naker-menu-button',
                    { onclick: () => { this.checkMenu(); } }),
                el('div.naker-menu-title', 'Menu'),
                this.menuList = el('div.naker-menu-list')
            ]),
            this.previusTitle2 = el('div.naker-title.naker-previus2-title'),
            this.previusTitle1 = el('div.naker-title.naker-previus1-title'),
            this.upButton = el('div.naker-button.naker-arrow-button.naker-button-up',
                { onclick: () => { this.showMeshVariantByNumber(this.currentNumber - 1); } },
                this.upArrow = el('div.naker-arrow')),
            this.meshTitle = el('div.naker-title.naker-mesh-title'),
            this.variantContainer = el('div.naker-variation'),
            this.materialTitle = el('div.naker-title.naker-material-title'),
            this.downButton = el('div.naker-button.naker-arrow-button.naker-button-down',
                { onclick: () => { this.showMeshVariantByNumber(this.currentNumber + 1); } },
                this.downArrow = el('div.naker-arrow')),
            this.nextTitle1 = el('div.naker-title.naker-next1-title'),
            this.nextTitle2 = el('div.naker-title.naker-next2-title'),
            this.hideButton = el('div.naker-button-hide',
                { onclick: () => { this.checkCollapse(); } },
                this.collapseArrow = el('div.naker-arrow')),
        ]);

        this.upArrow.innerHTML = ARROW_ICON;
        this.downArrow.innerHTML = ARROW_ICON;
        this.collapseArrow.innerHTML = ARROW_ICON;
        this.enterMenuButton.innerHTML = MENU_ICON;
        this.quitMenuButton.innerHTML = CROSS_ICON;

        mount(this.container, this.configurationContainer);
        this.hide();
    }

    private buildSummary() {
        this.summaryContainer = el('div.naker-summary-container', [
            this.title = el('div.naker-title',
                this.configurationTranslation.getTranslatedString('title')),
            this.subTitle = el('div.naker-subtitle',
                this.configurationTranslation.getTranslatedString('subtitle')),
            this.price = el('div.naker-price'),
            this.confirm = el('div.naker-confirm', this.configurationTranslation.getTranslatedString('confirm')),
        ]);
        mount(this.container, this.summaryContainer);

        this.exit = el('div.naker-button.naker-exit-button');
        this.exit.innerHTML = CROSS_ICON;
        mount(this.container, this.exit);
    }

    private addMeshPick(system: SystemCamera) {
        // In order to make sure this is a click and not a drag&drop action
        let clickAction = false;
        system.canvas.addEventListener('pointerdown', (evt) => {
            clickAction = true;
            setTimeout(() => {
                clickAction = false;
            }, 200);
        });
        system.canvas.addEventListener('pointerup', (evt) => {
            if (clickAction) {
                const mesh = system.pickMesh(evt);
                if (mesh) {
                    this.showMeshVariantByMeshes([mesh.name]);
                }
            }
        });
    }

    private menuActive = false

    private checkMenu() {
        if (!this.menuActive) {
            this.showMenu();
        } else {
            this.hideMenu();
        }
    }

    private showMenu() {
        setStyle(this.menu, this.visibleStyle);
        this.buildMenu();
        this.menuActive = true;
    }

    private hideMenu() {
        setStyle(this.menu, this.unvisibleStyle);
        this.menuActive = false;
    }

    private buildMenu() {
        const model = this.getModel();
        const variantMeshesMaterialsList = model.getVariantMeshesMaterialsList();
        const variantButtons = variantMeshesMaterialsList.map((v) => {
            const { nakerName } = v;
            const key = nakerName || v.meshes.join('');
            const name = this.configurationTranslation.getTranslatedString(key);
            const selected = model.getSelectedVariantMeshes(v.meshes);
            const color = (selected) ? this.system.toolMaterial.getColorFromMaterialKey(selected.material) : '';
            const button = el('div.naker-menu-mesh', {
                onclick: (e) => {
                    this.checkMenu();
                    this.showMeshVariantByMeshes(v.meshes);
                }
            }, [
                el('div.naker-combo',
                    el('div.naker-pin', { style: { background: color } })),
                el('div.naker-label', this.configurationTranslation.getTranslatedString(name)),
            ]);
            return button;
        });
        setChildren(this.menuList, variantButtons);
    }

    private collapsed = false

    private checkCollapse() {
        this.collapsed = !this.collapsed;
        setAttr(this.container, { 'data-naker-configurator-collapsed': this.collapsed });
        if (this.collapsed) { this.hideMenu(); }
    }

    private model: ContentModel

    public setModel(model: ContentModel): void {
        this.model = model;
    }

    private getModel(): ContentModel {
        if (this.model) return this.model;
        return this.contentsManager.getFirstModel();
    }

    private variantMeshesMaterialsList: IVariantMeshesProperties[]

    public showVariantMeshesMaterialsList(): void {
        setChildren(this.variantContainer, []);
        const model = this.getModel();
        this.variantMeshesMaterialsList = model.getVariantMeshesMaterialsList();
        this.showFirstVariantMesh();
    }

    public showFirstVariantMesh(): void {
        this.currentNumber = null;
        if (this.variantMeshesMaterialsList.length) this.showMeshVariantByNumber(0);
    }

    private currentNumber: number

    private showMeshVariantByNumber(num: number) {
        let numMinMax = Math.max(num, 0);
        numMinMax = Math.min(numMinMax, this.variantMeshesMaterialsList.length - 1);
        if (numMinMax === this.currentNumber) return;
        this.currentNumber = numMinMax;
        this.checkButtons();
        this.checkTitles();
        this.hideMenu();
        const variantMeshesMaterials = this.variantMeshesMaterialsList[this.currentNumber];
        if (variantMeshesMaterials) this.showMeshVariant(variantMeshesMaterials);
    }

    private showMeshVariantByMeshes(meshes: string[]) {
        for (let i = 0; i < this.variantMeshesMaterialsList.length; i++) {
            if (isEqual(this.variantMeshesMaterialsList[i].meshes, meshes)) {
                this.showMeshVariantByNumber(i);
                break;
            }
        }
    }

    private checkButtons() {
        const isFirst = this.currentNumber === 0;
        if (isFirst) setStyle(this.upButton, this.unvisibleStyle);
        else setStyle(this.upButton, this.visibleStyle);

        const isLast = this.currentNumber === this.variantMeshesMaterialsList.length - 1;
        if (isLast) setStyle(this.downButton, this.unvisibleStyle);
        else setStyle(this.downButton, this.visibleStyle);
    }

    private checkTitles() {
        const variantMeshesMaterials = this.variantMeshesMaterialsList[this.currentNumber];
        this.setTitleText(this.meshTitle, variantMeshesMaterials);
        const previusVariant1 = this.variantMeshesMaterialsList[this.currentNumber - 1];
        this.setTitleText(this.previusTitle1, previusVariant1);
        const previusVariant2 = this.variantMeshesMaterialsList[this.currentNumber - 2];
        this.setTitleText(this.previusTitle2, previusVariant2);
        const nextVariant1 = this.variantMeshesMaterialsList[this.currentNumber + 1];
        this.setTitleText(this.nextTitle1, nextVariant1);
        const nextVariant2 = this.variantMeshesMaterialsList[this.currentNumber + 2];
        this.setTitleText(this.nextTitle2, nextVariant2);
    }

    private setTitleText(title: HTMLElement, variantMeshesProperties: IVariantMeshesProperties) {
        if (!variantMeshesProperties || !variantMeshesProperties.meshes) {
            setStyle(title, { opacity: 0 });
        } else {
            const { nakerName } = variantMeshesProperties;
            const key = nakerName || variantMeshesProperties.meshes.join('');
            const text = this.configurationTranslation.getTranslatedString(key);
            setStyle(title, { opacity: 1 });
            title.textContent = text;
        }
    }

    private showMeshVariant(variantMeshesMaterials: IVariantMeshesMaterials) {
        // Put back default mode in case we were on text input
        setAttr(this.configurationContainer, { 'data-text-mode': 'variation' });
        const { meshes } = variantMeshesMaterials;
        const model = this.getModel();
        model.flashMeshes(meshes);
        const meshesProperties = model.getVariantMeshesPropertiesByMeshes(meshes);
        const system = this.contentsManager._system;
        const view = meshesProperties.cameraView;
        if (meshesProperties) {
            if (view) system.focusOnArcCameraAngles(view);
        }
        const materialListHtml = el('div.naker-combo-list',
            variantMeshesMaterials.materials.map((m) => {
                const rgb = this.system.toolMaterial.getColorFromMaterialKey(m);
                const materialHtml = el('div.naker-combo', {
                    onclick: (e) => {
                        setElementSelected(e.target.parentNode);
                        this.manageMaterialChange(variantMeshesMaterials, m);
                    }
                }, el('div.naker-pin', { style: { 'background-image': rgb } }));
                const isSelected = model.getSelectedVariantMeshes(meshes);
                if (isSelected) {
                    const isMaterialSelected = isEqual(isSelected.material, m);
                    if (isMaterialSelected) setAttr(materialHtml, { selected: true });
                }
                return materialHtml;
            }));
        setChildren(this.variantContainer, [materialListHtml]);
        const { decalGeometry } = variantMeshesMaterials;
        if (decalGeometry) {
            if (decalGeometry.type === 'text') {
                const html = this.addTextInput(variantMeshesMaterials);
                setChildren(this.variantContainer, [materialListHtml, html]);
            } else if (decalGeometry.type === 'mask') {
                const html = this.addMaskInput(variantMeshesMaterials.decalGeometry);
                setChildren(this.variantContainer, [html]);
            }
        }
        this.checkMaterialName(variantMeshesMaterials);
    }

    private manageMaterialChange(
        variantMeshesMaterials: IVariantMeshesProperties,
        material: string
    ) {
        const model = this.getModel();
        model.setVariantMeshes({ meshes: variantMeshesMaterials.meshes, material }, true);
        this.checkMaterialName(variantMeshesMaterials);
        this.notifyChange();
    }

    private notifyChange() {
        if (this.onChangeFunction) {
            const variant = this.getCurrentVariant();
            this.onChangeFunction(variant);
        }
    }

    public getCurrentVariant(): IVariantModel {
        const model = this.getModel();
        const variantMeshesList = model.getVariantMeshesList();
        const variantTextList = model.getVariantTextList();
        const variantMaskList = model.getVariantMaskList();
        return {
            name: '', variantMeshesList, variantTextList, variantMaskList
        };
    }

    private onChangeFunction: (variant: IVariantModel) => void

    public onChange(funct: (variant: IVariantModel) => void): void {
        this.onChangeFunction = funct;
    }

    public addTextInput(variantMeshes: IVariantMeshesProperties): HTMLElement[] {
        const model = this.getModel();
        const patternText = model.getPatternText(variantMeshes.meshes);
        if (!patternText) return [];
        const variantText = patternText.getVariant() as IVariantText;
        const { text } = variantText;
        const value = (text) || '';
        const textInput = el('input.naker-input', {
            placeholder: this.configurationTranslation.getTranslatedString('placeholder'),
            value,
            oninput: () => {
                const text = patternText.setText(textInput.value);
                setAttr(textInput, { value: text });
                this.notifyChange();
            }
        }) as HTMLInputElement;
        // Manage "Go" button on Androïd and hide the keyboard
        textInput.addEventListener('keydown', (e) => {
            const { key } = e;
            if (key === 'Enter') {
                textInput.setAttribute('readonly', 'true');
                setTimeout(() => {
                    textInput.blur();
                    textInput.removeAttribute('readonly');
                    textInput.removeAttribute('disabled');
                }, 100);
            }
        });
        const textChoice = el('div.naker-choice-list.naker-button-text-list', [
            el('div.naker-button-text', this.configurationTranslation.getTranslatedString('font'), {
                onclick: () => {
                    setAttr(this.configurationContainer, { 'data-text-mode': 'font' });
                }
            }),
            el('div.naker-button-text', this.configurationTranslation.getTranslatedString('material'), {
                onclick: () => {
                    setAttr(this.configurationContainer, { 'data-text-mode': 'combo' });
                }
            })
        ]);
        const textGeometry = variantMeshes.decalGeometry as ITextGeometry;
        const textElements = [textInput, textChoice];
        if (textGeometry.fonts) {
            const fontContainer = el('div.naker-font-list.naker-button-text-list',
                textGeometry.fonts.map((c) => el('div.naker-button-text', {
                    style: { 'font-family': c },
                    onclick: (e) => {
                        patternText.setFont(c);
                        this.notifyChange();
                    }
                }, 'ABC')));
            textElements.push(fontContainer);
        }

        let valid: HTMLElement; let
            cancel: HTMLElement;
        const textValid = el('div.naker-valid-list.naker-button-text-list', [
            cancel = el('div.naker-button-text.naker-cancel-button', {
                onclick: () => {
                    setAttr(this.configurationContainer, { 'data-text-mode': 'input' });
                }
            }),
            valid = el('div.naker-button-text.naker-valid-button', {
                onclick: () => {
                    setAttr(this.configurationContainer, { 'data-text-mode': 'input' });
                }
            }),
        ]);

        cancel.innerHTML = CROSS_ICON;
        valid.innerHTML = VALID_ICON;

        textElements.push(textValid);
        setAttr(this.configurationContainer, { 'data-text-mode': 'input' });
        return textElements;
    }

    private addMaskInput(variantMeshes: IVariantMeshesProperties): HTMLElement {
        const model = this.getModel();
        const maskGeometry = variantMeshes.decalGeometry as IMaskGeometry;
        const patternMask = model.getPatternMask(variantMeshes.meshes);
        if (!patternMask) return null;
        const maskVariant = patternMask.getVariant() as IVariantMask;
        const { mask } = maskVariant;
        const maskListHtml = el('div.naker-combo-list',
            maskGeometry.maskList.map((m) => {
                const materialHtml = el('div.naker-combo', {
                    onclick: (e) => {
                        setElementSelected(e.target.parentNode);
                        patternMask.setMask(m);
                        this.notifyChange();
                    }
                }, el('div.naker-pin', { style: { 'background-image': `url(${m})` } }));
                if (m === mask) setAttr(materialHtml, { selected: true });
                return materialHtml;
            }));
        return maskListHtml;
    }

    private checkMaterialName(variantMeshesMaterials: IVariantMeshesMaterials) {
        const model = this.getModel();
        const { meshes } = variantMeshesMaterials;
        const { decalGeometry } = variantMeshesMaterials;

        let selected = model.getSelectedVariantMeshes(meshes);
        if (!selected && decalGeometry) {
            selected = model.getSelectedVariantMeshes([decalGeometry.sourceMeshName]);
        }
        if (selected && selected.material) {
            const material = this.system.toolMaterial.getMaterialByKey(selected.material);
            const name = material.name || material.colorName;
            const tsString = this.configurationTranslation.getTranslatedString(name);
            this.materialTitle.textContent = tsString;
        } else {
            this.materialTitle.textContent = '';
        }
    }

    public updateTranslations(): void {
        this.title.textContent = this.configurationTranslation.getTranslatedString('title');
        this.subTitle.textContent = this.configurationTranslation.getTranslatedString('subtitle');
        this.confirm.textContent = this.configurationTranslation.getTranslatedString('confirm');
        this.checkTitles();
    }

    public setPrice(price: string): void {
        this.price.innerHTML = price;
    }

    public show(): void {
        setStyle(this.configurationContainer, { opacity: 1 });
    }

    public hide(): void {
        setStyle(this.configurationContainer, { opacity: 0 });
    }
}
